import { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import { Users, Sparkles, Heart, TreePine, ArrowRight, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import axios from "axios";
import { IMAGES, SITE_INFO } from "@/lib/constants";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6 },
};

const retreatTypes = [
  { icon: Sparkles, title: "Spiritual Retreats", desc: "Host transformational spiritual retreats in a peaceful, nature-surrounded setting." },
  { icon: TreePine, title: "Yoga Weekends", desc: "Perfect outdoor spaces for yoga, meditation, and wellness programming." },
  { icon: Users, title: "Family Reunions", desc: "Bring the whole family together for a memorable lakeside reunion." },
  { icon: Heart, title: "Private Celebrations", desc: "Birthdays, anniversaries, and milestone celebrations in an exclusive setting." },
];

export default function RetreatsPage() {
  const [form, setForm] = useState({
    name: "", email: "", phone: "", event_type: "", group_size: "", preferred_dates: "", message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.event_type) {
      toast.error("Please fill in required fields.");
      return;
    }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/inquiries`, form);
      toast.success(res.data.message);
      setForm({ name: "", email: "", phone: "", event_type: "", group_size: "", preferred_dates: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Retreats & Events | Private Resort Bookings Near Lake Texoma - Texoma Resorts</title>
        <meta name="description" content="Book the entire Texoma Resorts campus for spiritual retreats, yoga weekends, family reunions, corporate events, and private celebrations. Groups up to 40." />
      </Helmet>

      <section className="relative h-[50vh] min-h-[400px] flex items-end">
        <div className="absolute inset-0">
          <img src={IMAGES.yoga} alt="Outdoor retreat at Texoma Resorts" className="w-full h-full object-cover" />
          <div className="hero-overlay absolute inset-0" />
        </div>
        <div className="relative z-10 w-full max-w-7xl mx-auto px-6 md:px-12 pb-12">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <p className="caption-text text-white/60 mb-3">Group Experiences</p>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-7xl font-light text-white tracking-tight">
              Retreats & Events
            </h1>
          </motion.div>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp} className="max-w-3xl mb-16">
            <p className="caption-text text-accent mb-3">Book the Full Campus</p>
            <h2 className="font-heading text-3xl md:text-5xl font-light text-foreground tracking-tight mb-6">
              Your Private Resort Experience
            </h2>
            <p className="font-body text-base md:text-lg text-muted-foreground leading-relaxed">
              Serenity Village can be exclusively yours. Book the full campus for your
              retreat, reunion, or celebration. We accommodate groups of up to {SITE_INFO.maxGroupSize} guests
              with a variety of accommodation options.
            </p>
          </motion.div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-16">
            {retreatTypes.map((r, i) => (
              <motion.div key={r.title} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.1 }}>
                <Card className="border border-border/50 h-full card-lift">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 mb-4 rounded-full bg-primary/5 flex items-center justify-center">
                      <r.icon size={22} className="text-primary" />
                    </div>
                    <h3 className="font-heading text-lg font-semibold text-foreground mb-2">{r.title}</h3>
                    <p className="font-body text-sm text-muted-foreground leading-relaxed">{r.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      <section data-testid="retreat-inquiry-form" className="py-16 md:py-24 bg-muted/30">
        <div className="max-w-2xl mx-auto px-6 md:px-12">
          <motion.div {...fadeUp}>
            <p className="caption-text text-accent mb-3">Get in Touch</p>
            <h2 className="font-heading text-3xl md:text-4xl font-normal text-foreground mb-8">
              Event Inquiry Form
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-body text-sm">Full Name *</Label>
                  <Input
                    data-testid="retreat-name"
                    value={form.name}
                    onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="font-body mt-1"
                  />
                </div>
                <div>
                  <Label className="font-body text-sm">Email *</Label>
                  <Input
                    data-testid="retreat-email"
                    type="email"
                    value={form.email}
                    onChange={(e) => setForm({ ...form, email: e.target.value })}
                    className="font-body mt-1"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-body text-sm">Phone</Label>
                  <Input
                    data-testid="retreat-phone"
                    value={form.phone}
                    onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    className="font-body mt-1"
                  />
                </div>
                <div>
                  <Label className="font-body text-sm">Event Type *</Label>
                  <Select value={form.event_type} onValueChange={(val) => setForm({ ...form, event_type: val })}>
                    <SelectTrigger data-testid="retreat-event-type" className="font-body mt-1">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="spiritual_retreat">Spiritual Retreat</SelectItem>
                      <SelectItem value="yoga_weekend">Yoga Weekend</SelectItem>
                      <SelectItem value="family_reunion">Family Reunion</SelectItem>
                      <SelectItem value="corporate_event">Corporate Event</SelectItem>
                      <SelectItem value="private_celebration">Private Celebration</SelectItem>
                      <SelectItem value="group_getaway">Group Getaway</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="font-body text-sm">Estimated Group Size</Label>
                  <Input
                    data-testid="retreat-group-size"
                    value={form.group_size}
                    onChange={(e) => setForm({ ...form, group_size: e.target.value })}
                    placeholder="e.g., 20 people"
                    className="font-body mt-1"
                  />
                </div>
                <div>
                  <Label className="font-body text-sm">Preferred Dates</Label>
                  <Input
                    data-testid="retreat-dates"
                    value={form.preferred_dates}
                    onChange={(e) => setForm({ ...form, preferred_dates: e.target.value })}
                    placeholder="e.g., March 15-17, 2026"
                    className="font-body mt-1"
                  />
                </div>
              </div>
              <div>
                <Label className="font-body text-sm">Tell Us About Your Event</Label>
                <Textarea
                  data-testid="retreat-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  className="font-body mt-1"
                  rows={4}
                />
              </div>
              <Button
                type="submit"
                data-testid="retreat-submit"
                disabled={loading}
                className="w-full bg-primary hover:bg-primary/90 text-white font-body rounded-full"
              >
                {loading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                Submit Inquiry
              </Button>
            </form>
          </motion.div>
        </div>
      </section>
    </>
  );
}
